/**
 * Durvey's own interface in the user's language: the system level (docs/plans/INTERFACE_LANGUAGES_PLAN.md,
 * D-S1). Texts are marked with Angular's i18n (`i18n` in templates, `$localize` in TypeScript); a
 * language's translations are loaded before the code that uses them is imported, so every message —
 * also one built when a module loads — comes out translated. One build serves every language;
 * switching reloads the page.
 *
 * The catalogues are split where the app is (scripts/i18n-catalogues.mjs, plan §8 S0 results): `app`
 * (sign-in, public pages, shared parts) loads before main.ts imports the app; `creator` loads with the
 * creator module (loadArea in its route) and is fetched from the start, beside the app's code.
 *
 * The survey level is separate: the participant survey keeps its own tables
 * (participant-survey/i18n/strings.ts), in the study's language whatever the creator's is.
 *
 * Until the account carries a language (S1: User.language), the choice is a browser setting for
 * trying it out (`durvey.systemLanguage`, or `?systemLanguage=de` once). `?systemLanguage=en-XA` is the
 * pseudo-language (plan §3, guard 5): every marked text accented, longer and bracketed, so text that
 * isn't marked, or doesn't fit, shows on any screen.
 */
import { registerLocaleData } from '@angular/common';
import { loadTranslations } from '@angular/localize';

import { BUILD_INFO } from 'src/environments/build-info';
import { loadLibraryTexts } from './system-locale';

const STORAGE_KEY = 'durvey.systemLanguage';
const SOURCE = 'en';
const PSEUDO = 'en-XA';

/** Angular's locale data per registry code (backend/i18n/languages.json): dates, numbers and plural
 *  rules for the pipes. Listed one by one so the bundler can split them into their own chunks. */
const LOCALE_DATA: Readonly<Record<string, () => Promise<{ default: unknown[] }>>> = {
  de: () => import('@angular/common/locales/de'),
  fr: () => import('@angular/common/locales/fr'),
  es: () => import('@angular/common/locales/es'),
  it: () => import('@angular/common/locales/it'),
  'pt-PT': () => import('@angular/common/locales/pt-PT'),
  'pt-BR': () => import('@angular/common/locales/pt'),
  nl: () => import('@angular/common/locales/nl'),
  pl: () => import('@angular/common/locales/pl'),
  sv: () => import('@angular/common/locales/sv'),
  da: () => import('@angular/common/locales/da'),
  nb: () => import('@angular/common/locales/nb'),
  fi: () => import('@angular/common/locales/fi'),
  cs: () => import('@angular/common/locales/cs'),
  el: () => import('@angular/common/locales/el'),
  tr: () => import('@angular/common/locales/tr'),
};

/** The parts of the app with a catalogue of their own. */
export type SystemArea = 'app' | 'creator';

/** A catalogue as scripts/i18n-catalogues.mjs builds it from the language's XLIFF file. */
export interface SystemCatalogue {
  locale: string;
  translations: Record<string, string>;
}

export type CatalogueFetcher = (code: string, area: SystemArea) => Promise<SystemCatalogue | null>;

let active = SOURCE;
let fetcher: CatalogueFetcher = fetchFromAssets;
const areas = new Map<SystemArea, Promise<void>>();

/** The language the interface is shown in: a registry code (or en-XA), English when nothing else loaded. */
export function systemLanguage(): string {
  return active;
}

/** The locale for dates and numbers (LOCALE_ID): the language's own, English for the pseudo-language. */
export function systemLocale(): string {
  return active === PSEUDO ? SOURCE : active;
}

const known = (code: string | null | undefined): code is string =>
  !!code && (code === SOURCE || code === PSEUDO || code in LOCALE_DATA);

/** The language to start in: `?systemLanguage=` (kept), else the stored choice, else English. */
export function chosenLanguage(search = location.search, storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeStorage()): string {
  const asked = new URLSearchParams(search).get('systemLanguage');
  if (known(asked)) {
    storage?.setItem(STORAGE_KEY, asked);
    return asked;
  }
  const stored = storage?.getItem(STORAGE_KEY);
  return known(stored) ? stored : SOURCE;
}

const FOLLOWED_KEY = 'durvey.systemLanguage.followed';

/**
 * The account's language wins (User.language, chosen in Account › Profile; S1): when it isn't the one
 * showing, it becomes this browser's choice and the page reloads into it. Once per language and tab
 * session, so a catalogue that can't be loaded leaves English rather than reloading again and again.
 * An account without a language leaves the browser's choice as it is.
 */
export function followAccountLanguage(language: string | null | undefined,
                                      reload: () => void = () => location.reload(),
                                      storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeStorage(),
                                      session: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = safeSession()): void {
  if (!known(language) || language === PSEUDO) return;
  if (language === active) {
    session?.removeItem(FOLLOWED_KEY);
    return;
  }
  storage?.setItem(STORAGE_KEY, language);
  if (session?.getItem(FOLLOWED_KEY) === language) return;
  session?.setItem(FOLLOWED_KEY, language);
  reload();
}

/**
 * Load `code`'s `app` catalogue and locale data, and start fetching its `creator` catalogue. English,
 * or a language that fails to load, leaves the interface in English: a missing message always shows
 * its English text, never an id.
 */
export async function loadSystemLanguage(code: string = chosenLanguage(), fetchCatalogue: CatalogueFetcher = fetchFromAssets): Promise<string> {
  if (code === SOURCE || !known(code)) return active;
  fetcher = fetchCatalogue;
  try {
    const [catalogue, locale] = await Promise.all([
      fetchCatalogue(code, 'app'),
      code === PSEUDO ? Promise.resolve(null) : LOCALE_DATA[code](),
      loadLibraryTexts(code),   // PrimeNG's own words (system-locale.ts)
    ]);
    if (!catalogue?.translations) return active;
    loadTranslations(catalogue.translations);
    if (locale) registerLocaleData(locale.default, code);
    active = code;
    areas.set('app', Promise.resolve());
    void loadArea('creator');   // fetched beside the app's code, applied when the creator module is imported
  } catch {
    // English stays.
  }
  return active;
}

/**
 * The area's catalogue in the active language, loaded once; resolves when its messages are in.
 * Used before the area's module is imported (its route's loadChildren). Never fails: a catalogue
 * that can't be had leaves that area's texts in English.
 */
export function loadArea(area: SystemArea): Promise<void> {
  if (active === SOURCE) return Promise.resolve();
  let pending = areas.get(area);
  if (!pending) {
    const code = active;
    pending = fetcher(code, area)
      .then(catalogue => { if (catalogue?.translations && active === code) loadTranslations(catalogue.translations); })
      .catch(() => undefined);
    areas.set(area, pending);
  }
  return pending;
}

/** One choice in the account's language picker: the language in its own name. */
export interface InterfaceChoice {
  code: string;
  label: string;
  /** Machine-translated, not yet checked by a native reader (D-S5): the picker says so. */
  machine: boolean;
}

/** The languages the interface can be shown in, English first, each named in itself ("Deutsch"). */
export function interfaceChoices(languages: { code: string; endonym: string; system?: string }[]): InterfaceChoice[] {
  const offered = languages.filter(l => l.code === SOURCE || (l.system && l.system !== 'off' && known(l.code)));
  // i18n-ignore: a language's name in itself reads the same in every interface language.
  if (!offered.some(l => l.code === SOURCE)) offered.unshift({ code: SOURCE, endonym: 'English', system: 'reviewed' });
  return offered
    .sort((a, b) => (a.code === SOURCE ? -1 : b.code === SOURCE ? 1 : 0))
    .map(l => ({ code: l.code, label: l.endonym, machine: l.code !== SOURCE && l.system !== 'reviewed' }));
}

/** A language's name in the interface's language ("Französisch"); the registry's English name in English. */
export function languageName(code: string, english: string): string {
  if (systemLocale() === SOURCE) return english;
  try {
    return new Intl.DisplayNames([active], { type: 'language' }).of(code) ?? english;
  } catch {
    return english;
  }
}

/** "French, Spanish and Greek" in the interface's language. */
export function listOf(items: string[]): string {
  return new Intl.ListFormat(systemLocale(), { style: 'long', type: 'conjunction' }).format(items);
}

async function fetchFromAssets(code: string, area: SystemArea): Promise<SystemCatalogue | null> {
  // Each build names its catalogues anew: a cached one from an earlier deploy has other message ids.
  const url = `assets/i18n/system/${encodeURIComponent(code)}/${area}.json?v=${encodeURIComponent(BUILD_INFO.commit)}`;
  const response = await fetch(url);
  return response.ok ? response.json() as Promise<SystemCatalogue> : null;
}

function safeStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

/** For specs: back to English. */
export function resetSystemLanguageForTests(): void {
  active = SOURCE;
  fetcher = fetchFromAssets;
  areas.clear();
}
